import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Success from "@washingtonpost/wpds-assets/asset/success";
import Link from "@washingtonpost/wpds-assets/asset/link";
import { Button } from "@washingtonpost/wpds-button";
import { Icon } from "@washingtonpost/wpds-icon";
import "../index.css";
import { copyTextToClipboard } from "./utils";
import { styled } from "@washingtonpost/wpds-theme";

const StyledText = styled("span", {
  position: "relative",
  left: "2px",
  fontSize: "$075",
  padding: "4px 6px",
  color: "white",
  borderRadius: "5px",
  opacity: 0,
  transition: "opacity 0.3s ease-in-out",
  pointerEvents: "none",
  backgroundColor: "rgba(42, 42, 42, .8)"
});

/**
 * Component that displays Copy Code Button.
 * @returns A button containing the wpds link icon.
 */
export const CopyButton = ({ content, onBlur = () => {}, isMiniPlayer }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Clear copied message after 3 seconds
  useEffect(() => {
    if (showConfirmation === false) {
      return;
    }

    const timeoutId = setTimeout(() => setShowConfirmation(false), 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [showConfirmation]);

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      copyTextToClipboard(content, () => setShowConfirmation(true));
    },
    [content]
  );

  return (
    <>
      <Button
        aria-label={`Copy link`}
        icon="center"
        className={"focus-visible-highlight"}
        variant="primary"
        css={{
          border: "none",
          background: "none",
          "&:hover": {
            background: "none"
          },
          margin: "-$075",
          padding: "$075"
        }}
        onClick={handleClick}
        onBlur={onBlur}
      >
        <Icon fill="white" size={isMiniPlayer ? "100" : "150"}>
          {showConfirmation ? <Success /> : <Link />}
        </Icon>
      </Button>
      <StyledText
        css={{
          ...(showConfirmation && { opacity: 1 })
        }}
      >
        Link copied
      </StyledText>
    </>
  );
};

CopyButton.displayName = "CopyButton";

CopyButton.propTypes = {
  /** The content to copy to the clipboard. */
  content: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  isMiniPlayer: PropTypes.bool.isRequired
};
